import React, {useState} from 'react';
import {
  Grid,
  TextField, FormControl, Checkbox, FormControlLabel, Box, Button
} from '@material-ui/core';
import {DeleteOutlined, InsertLink} from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import AddressTextField from "../../shared/Inputs/AddressTextField";
import MapIcon from "@material-ui/icons/Map";
import {AddressSuggestionChip} from "../../shared/Chips";
import SingleAutoComplete from "../../shared/SingleAutoComplete";
import AdminUserService from "../../services/AdminUserService";
import TextInput from "../../sharedFormComponents/TextInput";
import {useFieldWithValidation} from "../../../../hooks";
import {validPhoneNumber} from "../../../../helpers/utils";
import useAdminUserSession from "../../../../hooks/useAdminUserSession";
import {CopyToClipboard} from "react-copy-to-clipboard/src";
import CustomSnackbar from "../../../user/shared/CustomSnackbar";

const useStyles = makeStyles((theme) => ({
  root: {},
  fieldsRow: {
    marginBottom: '12px',
  },
  removeLocation: {
    padding: '16px 0',
    marginBottom: '8px',
    cursor: "pointer"
  }
}))

const TeamLocationsFields = ({
                               location,
                               locationOptions,
                               handleNestedFieldChange,
                               handleRemoveNestedFields,
                               isPrincipal,
                               handleSetPrincipalLocation,
                               showRemoveLocation = true,
                               onRemoveLocation,
                               ...props
                             }) => {
  const classes = useStyles();

  const [locationSuggestion, setLocationSuggestion] = useState({});
  const [snackBarMessage, setSnackBarMessage] = useState({show: false, message: '', severity: 'success'})
  const {reset: resetPhoneNumber, validate: validatePhoneNumber, ...phone_number} = useFieldWithValidation(
      "text",
      location.phone_number || "",
      (value) => (validPhoneNumber(value)),
      false,
      ({target:{ name, value }}) => handleOnChangeAutocomplete(name, value),
      (value) => (value && !validPhoneNumber(value)) ? "Invalid Phone number." : null
  );
  const {session} = useAdminUserSession();

  const handleOnChangeAutocomplete = (field, selectedValue) => {
    handleNestedFieldChange('locations', location, field, selectedValue);
  }

  const handleCountryChange = (country) => {
    if (country) {
      if (locationOptions.unitedStatesId === country.id) {
        handleOnChangeAutocomplete('state', null)
        handleOnChangeAutocomplete('state_id', null)
        handleOnChangeAutocomplete('city', null)
        handleOnChangeAutocomplete('city_id', null)
      }
      handleOnChangeAutocomplete('country', country.name)
      handleOnChangeAutocomplete('country_id', country.id)
    }else {
      handleOnChangeAutocomplete('country', '')
      handleOnChangeAutocomplete('country_id', null)
    }
  }

  const handleCheckChange = (event) => {
    event.persist();

    const isChecked = event.target.checked;
    const locationIndex = props.index;

    handleSetPrincipalLocation(locationIndex, isChecked);
  }

  const handleIgnoreCheckChange = (event) => {
    event.persist();

    const isChecked = event.target.checked;
    handleOnChangeAutocomplete('ignore_validation', isChecked)
  }

  const handleStateChange = (state) => {
    handleOnChangeAutocomplete('state', state ? state.id : null)
  }

  const handleCityChange = (city) => {
    handleOnChangeAutocomplete('city', city ? city.name : '')
    handleOnChangeAutocomplete('city_id', city ? city.id : null)
  }

  const handleSuggestion = (address) => {
    setLocationSuggestion(address);
    AdminUserService.logMapboxUsage(session.id, session);
  }

  const handleOnChange = (event) => {
    event.persist();

    handleOnChangeAutocomplete(event.target.name, event.target.value)
  }

  const onCopy = () => {
    setSnackBarMessage({show: true, message: 'Address Copied', severity: 'success'})
  }

  const handleCloseSnackbar = () => {
    setSnackBarMessage({show: false, message: '', severity: 'success'})
  }

    const { id, _destroy } = location;

    return (
      <React.Fragment>
        {_destroy ? null : (
          <Grid container spacing={2} key={id} className={classes.fieldsRow}>
            <Grid item container spacing={2} xs={10}>
              <Grid item xs={2}>
                <TextInput
                    {...phone_number}
                    label={"Phone Number"}
                    name={"phone_number"}
                    maxLength={150}
                />
              </Grid>

              <Grid
                  item
                  xs={2}
              >
                <AddressTextField
                    label='Address'
                    variant="outlined"
                    name='address'
                    inputProps={{ maxLength: 500 }}
                    value={location.address || ''}
                    Icon={MapIcon}
                    onBlur={handleOnChange}
                    onAction={(address) => {handleSuggestion(address)}}
                    addressInfo={{
                      country: locationOptions.countries.find(a => a.id == location?.country_id)?.name ,
                      state: location.state,
                      city: locationOptions.cities.find(a => a.id == location?.city_id)?.name ,
                      postCode: location.zip_code,
                    }}
                    disabled={!!location.branch_id}
                    required={!location.ignore_validation}
                    disableSuggestion={!!location.ignore_validation}
                />

                <AddressSuggestionChip
                    show={locationSuggestion.valid !== undefined}
                    valid={locationSuggestion.valid}
                    value={location.address}
                    suggested={locationSuggestion.address}
                />
              </Grid>

              <Grid item xs={1}>
                <TextField
                    label='Address 2'
                    variant="outlined"
                    name='address_2'
                    inputProps={{ maxLength: 40  }}
                    value={location.address_2 || ''}
                    onChange={handleOnChange}
                    disabled={!!location.branch_id}
                />
              </Grid>

              <Grid item xs={1}>
                <FormControl fullWidth>
                  <TextField
                      label="Branch Id"
                      variant="outlined"
                      name='branch_id'
                      disabled={true}
                      inputProps={{ maxLength: 10  }}
                      value={location.branch_id || ''}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={2}>
                <SingleAutoComplete
                    options={locationOptions.countries}
                    handleOnChange={(value) => handleCountryChange(value)}
                    handleOnError={() => {}}
                    selectedId={location.country_id}
                    field={'country'}
                    label={"Country"}
                    variant={"outlined"}
                    className="entity-autocomplete"
                    autoHighlight
                    required={!location.ignore_validation}
                    disabled={!!location.branch_id}
                />
              </Grid>

              {
                  location.country_id === locationOptions.unitedStatesId && <Grid item xs={2}>
                    <SingleAutoComplete
                        options={locationOptions.states}
                        handleOnChange={(value) => handleStateChange(value)}
                        handleOnError={() => {}}
                        selectedId={location.state}
                        field={'state'}
                        label={"State"}
                        variant={"outlined"}
                        className="entity-autocomplete"
                        autoHighlight
                        disabled={!!location.branch_id}
                        required={!location.ignore_validation}
                    />

                    <AddressSuggestionChip
                        show={locationSuggestion.valid}
                        valid={locationSuggestion.valid}
                        value={ locationOptions.states.find(e => e.id == location.state)?.name }
                        suggested={locationSuggestion.state}
                    />
                  </Grid>
              }

              <Grid item xs={2}>
                <SingleAutoComplete
                    options={locationOptions.cities}
                    handleOnChange={(value) => handleCityChange(value)}
                    handleOnError={() => {}}
                    selectedId={location.city_id}
                    field={'city'}
                    label={"City"}
                    variant={"outlined"}
                    className="entity-autocomplete"
                    autoHighlight
                    disabled={!!location.branch_id}
                    required={!location.ignore_validation}
                />

                <AddressSuggestionChip
                    show={locationSuggestion.valid}
                    valid={locationSuggestion.valid}
                    value={locationOptions.cities.find(a => a.id == location?.city_id)?.name }
                    suggested={locationSuggestion.city}
                />
              </Grid>
            </Grid>
            <Grid item container xs={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                      label="Zip Code"
                      variant="outlined"
                      name='zip_code'
                      inputProps={{ maxLength: 40  }}
                      value={location.zip_code || ''}
                      onChange={handleOnChange}
                      disabled={!!location.branch_id}
                      required={!location.ignore_validation}
                  />
                  <CopyToClipboard onCopy={onCopy}
                                   text={`${location?.address || ''}, ${locationOptions.cities.find(a => a.id === location?.city_id)?.name || ''}, ${locationOptions.states.find(e => e.id === location?.state)?.name || ''} ${location?.zip_code || ''}`}>
                    <Button color="primary" variant="outlined" style={{marginTop: '5px'}}>
                    <span style={{
                      display: 'flex',
                      whiteSpace: 'nowrap',
                      textTransform: 'capitalize',
                      fontSize: '12px',
                    }}>
                      Copy Address
                      <InsertLink style={{width: '20px', margin: '-1px 0 0 3px'}}/>
                    </span>
                    </Button>
                  </CopyToClipboard>
                </FormControl>

                <AddressSuggestionChip
                    show={locationSuggestion.valid}
                    valid={locationSuggestion.valid}
                    value={location.zip_code}
                    suggested={locationSuggestion.zipCode}
                />
              </Grid>
              <Grid
                  item
                  xs={6}
              >
                <Box display={"flex"}>
                  <FormControlLabel
                      control={
                        <Checkbox
                            checked={isPrincipal}
                            onChange={handleCheckChange}
                            name="principal"
                        />}
                      label="Primary"
                      style={{marginRight: "0", padding: "8px"}}
                  />
                  {
                    showRemoveLocation &&
                      <DeleteOutlined style={{marginTop: "16px"}} onClick={() => onRemoveLocation(location)} />
                  }
                </Box>
                <Box display={"flex"}>
                  <FormControlLabel
                      control={
                        <Checkbox
                            checked={!!location.ignore_validation}
                            onChange={handleIgnoreCheckChange}
                            name="ignore_validation"
                        />}
                      label="Skip Validation"
                      style={{marginRight: "1", paddingLeft: "8px"}}
                  />
                </Box>
              </Grid>
            </Grid>
            {snackBarMessage.show ?
                <CustomSnackbar
                    open={snackBarMessage.show}
                    closeHandler={handleCloseSnackbar}
                    message={snackBarMessage.message}
                    options={{
                      severity: snackBarMessage.severity
                    }}
                />
                : null}
          </Grid>
        )}
      </React.Fragment>
    )
}

export default TeamLocationsFields;
